import { MASTERY } from './config.ts';
import type { MasterySkill, MasteryState } from './types.ts';

export function emptyMastery(character: string, skill: MasterySkill): MasteryState {
  return { character, skill, score: 0, attempts: 0, correct: 0, lastAt: null, dueAt: null };
}

/** Gains shrink as the score rises; losses shrink as it falls. Never leaves 0..100. */
export function nextScore(score: number, correct: boolean): number {
  if (correct) return Math.min(100, score + Math.round((100 - score) * 0.35) + 5);
  return Math.max(0, score - Math.round(score * 0.3) - 5);
}

export function intervalDays(score: number, correct: boolean): number {
  if (!correct) return MASTERY.failureIntervalDays;
  for (const band of MASTERY.intervalsByScore) if (score < band.below) return band.days;
  return MASTERY.intervalsByScore[MASTERY.intervalsByScore.length - 1]!.days;
}

export function applyResult(state: MasteryState, correct: boolean, now: Date): MasteryState {
  const score = nextScore(state.score, correct);
  const due = new Date(now.getTime() + intervalDays(score, correct) * 86_400_000);
  return {
    ...state,
    score,
    attempts: state.attempts + 1,
    correct: state.correct + (correct ? 1 : 0),
    lastAt: now.toISOString(),
    dueAt: due.toISOString(),
  };
}

export type MasteryMap = Map<string, MasteryState>; // key `${char}:${skill}`

export const masteryKey = (character: string, skill: MasterySkill) => `${character}:${skill}`;

export function getScore(map: MasteryMap, character: string, skill: MasterySkill): number {
  return map.get(masteryKey(character, skill))?.score ?? 0;
}

/** Spec: read ≥ 80 and listen ≥ 80, plus write_memory ≥ 70 for write targets. */
export function isMastered(map: MasteryMap, character: string, isWriteTarget: boolean): boolean {
  const ok =
    getScore(map, character, 'read') >= MASTERY.masteredRead &&
    getScore(map, character, 'listen') >= MASTERY.masteredListen;
  if (!ok) return false;
  return !isWriteTarget || getScore(map, character, 'write_memory') >= MASTERY.masteredWrite;
}

/** Lowest score across the skills that matter for this character (used to weight reviews). */
export function weakestScore(map: MasteryMap, character: string, isWriteTarget: boolean): number {
  const skills: MasterySkill[] = ['listen', 'read', 'meaning', 'speak', 'pinyin', 'sentence'];
  if (isWriteTarget) skills.push('write_guided', 'write_memory');
  return Math.min(...skills.map((s) => getScore(map, character, s)));
}
